import { useEffect, useRef } from 'react';

/**
 * Fecha um menu ao clicar fora dele ou apertar Esc.
 *
 * ================================================================
 * POR QUE NÃO A CAMADA INVISÍVEL `fixed inset-0`
 * ================================================================
 *
 * O padrão comum é cobrir a tela com uma div transparente que fecha o menu no
 * clique. Aqui ele FALHAVA de um jeito difícil de enxergar: a barra superior
 * usa `backdrop-blur`, e um elemento com `backdrop-filter` vira bloco de
 * contenção para descendentes `position: fixed`. A camada, filha do cabeçalho,
 * deixava de ser relativa à janela e passava a cobrir só a faixa de 56px da
 * barra — clicar no corpo da página não fechava nada.
 *
 * Nenhum ajuste de `z-index` resolveria: o problema é de contenção, não de
 * empilhamento. Escutar o documento não depende de nenhum dos dois.
 *
 * `pointerdown` em vez de `click`: o menu fecha assim que o dedo ou o botão
 * desce, sem esperar o `mouseup`. E na fase de CAPTURA, para que o menu já
 * esteja fechado antes de o elemento clicado atrás dele reagir.
 */
export function useDismiss<T extends HTMLElement>(
  open: boolean,
  onClose: () => void,
) {
  const ref = useRef<T>(null);

  // O callback vive num ref para que o efeito não seja remontado a cada
  // render só porque o chamador escreveu `() => setOpen(false)` em linha.
  const handler = useRef(onClose);
  handler.current = onClose;

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler.current();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') handler.current();
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  /** Prender no elemento que envolve o botão E o painel. */
  return ref;
}
